import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import qs, { ParsedQs } from 'qs';
import useStableLocation from './useStableLocation';

/**
 * When the location.search parameter changes:
 * - Parse it into a URLSearchParams instance
 * - Convert it into a Record<string, string>
 * When setting a new location.search parameter:
 * - Convert it into a URLSearchParams instance
 * - Merge it with the current location.search (for things like ?tab=groups)
 * - Handle fields that have been removed (since they'll be in the merged state)
 *
 * We need to ensure that updating a parameter doesn't cause a render loop by updating the URL
 * and then updating the state, which triggers a re-render, which updates the URL, etc.
 *
 * We also need to ensure that the values we return for each field are stable, so that we don't
 * trigger unnecessary re-renders. This means that we should memoize the values we return.
 */
const useSyncWithUrlSearchParams = (): UseSyncWithUrlSearchParamsResult => {
  const history = useHistory();
  const location = useStableLocation();

  const parsedParams = useMemo<ParsedQs>(
    () => qs.parse(location.search, { ignoreQueryPrefix: true }),
    [location.search],
  );

  // We have to store some values in refs so that we can keep setUrlSearchParams as a consistent
  // function reference. This is necessary because we need to ensure the callback usage always has
  // the latest values, but we need to ensure the function can be used in a hook dependency too.
  const locationRef = useRef(location);
  const parsedParamsRef = useRef(parsedParams);
  useEffect(() => {
    locationRef.current = location;
  }, [location]);
  useEffect(() => {
    parsedParamsRef.current = parsedParams;
  }, [parsedParams]);

  const setUrlSearchParams = useCallback(
    (state: ParsedQs | ((prevState: ParsedQs) => ParsedQs)) => {
      const newState = typeof state === 'function' ? state(parsedParamsRef.current) : state;
      const next = qs.stringify(newState, { encodeValuesOnly: true });
      if (next !== locationRef.current.search) {
        history.replace({ ...locationRef.current, search: next });
      }
    },
    [history],
  );

  // eslint-disable-next-line @eventmobi/no-array-return-in-hook
  return [parsedParams, setUrlSearchParams];
};

type UseSyncWithUrlSearchParamsResult = [
  ParsedQs,
  (state: ParsedQs | ((prevState: ParsedQs) => ParsedQs)) => void,
];

export default useSyncWithUrlSearchParams;
